import { useEffect, useState, type FormEvent } from 'react';
import { feedback, type BugReportsDto } from '../net/api';
import { CLIENT_VERSION, recentContext } from '../net/telemetry';
import { Sheet } from './Sheet';

const STATUS: Record<string, string> = {
  open: 'Waiting for the team',
  in_progress: 'Being looked at',
  fixed: 'Fixed!',
  wont_fix: 'Closed',
  duplicate: 'Already known',
  not_a_bug: 'Closed',
};

/**
 * "Report a bug". Sends what the player wrote plus what the game knows (where
 * they are, recent errors and failed requests, the build) and, if they like, a
 * picture of the game taken when the panel opened.
 */
export function BugReport({ area, panel, screenshot, onClose }: { area: string; panel?: string; screenshot?: string; onClose: () => void }) {
  const [info, setInfo] = useState<BugReportsDto>();
  const [attach, setAttach] = useState(!!screenshot);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<number>();
  useEffect(() => {
    feedback.list().then(setInfo, (e: Error) => setError(e.message));
  }, [sent]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(undefined);
    try {
      const r = await feedback.send({
        category: String(f.get('category')),
        description: String(f.get('description') ?? ''),
        area,
        clientVersion: CLIENT_VERSION,
        context: { path: window.location.pathname, viewport: `${window.innerWidth}×${window.innerHeight}`, panel, ...recentContext() },
        screenshot: attach ? screenshot : undefined,
      });
      setSent(r.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Report a bug" subtitle="Found something broken or weird? Tell us and we'll look into it." onClose={onClose}>
      {sent ? (
        <div className="stack-sm">
          <p className="note-ok" role="status">Thank you! Your report (#{sent}) is on its way to the Buddy Bay team.</p>
          <div className="row-wrap">
            <button className="btn" onClick={onClose}>Back to the game</button>
            <button className="btn ghost" onClick={() => setSent(undefined)}>Report something else</button>
          </div>
        </div>
      ) : (
        <form className="stack-sm" onSubmit={submit}>
          <label className="field">
            <span>What kind of problem is it?</span>
            <select name="category" id="bug-category" required defaultValue="bug">
              {(info?.categories ?? [{ key: 'bug', label: 'Something is broken' }]).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          <label className="field">
            <span>What happened? What were you trying to do?</span>
            <textarea name="description" id="bug-description" rows={5} minLength={10} maxLength={2000} required placeholder="For example: I bought a hat but it isn't in my backpack." />
          </label>
          <p className="muted small-print">Please don't include personal information like your address, phone number or passwords.</p>
          {screenshot && (
            <label className="check-row">
              <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.currentTarget.checked)} />
              <span>Attach a picture of the game</span>
            </label>
          )}
          {screenshot && attach && <img className="bug-preview" src={screenshot} alt="Picture of the game that will be attached" />}
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="btn wide" disabled={busy}>{busy ? 'Sending…' : 'Send report'}</button>
          <p className="muted small-print">We also send the game version ({CLIENT_VERSION}), your browser, and any errors the game noticed, to help us fix it.</p>
        </form>
      )}
      {!!info?.mine.length && (
        <section className="stack-sm my-reports">
          <h3 className="mini-head">Your reports</h3>
          <ul className="plain">
            {info.mine.map((r) => (
              <li key={r.id}>
                <span className={r.status === 'fixed' ? 'tag ok' : 'tag'}>{STATUS[r.status] ?? r.status}</span> <span className="muted">#{r.id} · {new Date(r.createdAt).toLocaleDateString()}</span>
                <div className="small-print">{r.description}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Sheet>
  );
}

/** A JPEG of the Phaser game view, at most 1280 px wide (or undefined if there's no game canvas). */
export function snapshotGame(game: { renderer: { snapshot: (cb: (img: HTMLImageElement | unknown) => void, type?: string, q?: number) => void } } | undefined): Promise<string | undefined> {
  if (!game) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(undefined), 1500);
    try {
      game.renderer.snapshot((img) => {
        window.clearTimeout(timer);
        if (!(img instanceof HTMLImageElement)) return resolve(undefined);
        const done = () => {
          const scale = Math.min(1, 1280 / img.naturalWidth);
          const c = document.createElement('canvas');
          c.width = Math.round(img.naturalWidth * scale);
          c.height = Math.round(img.naturalHeight * scale);
          c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
          resolve(c.toDataURL('image/jpeg', 0.7));
        };
        if (img.complete) done();
        else img.onload = done;
      });
    } catch {
      window.clearTimeout(timer);
      resolve(undefined);
    }
  });
}
